<template>
  <div>
    <RevDrawer
      :name="MODAL_NAMES.GRADING_WITH_IA"
      :title="i18n(translations.gradeWithAIModalTitle)"
    >
      <template #body>
        <RevCard
          class="flex flex-col items-center justify-center bg-gradient-to-b from-[#7b3dbe] to-[#ffffff] px-16 py-28"
        >
          <h3
            class="text-static-default-hi punchline text-center text-[42px] leading-none"
          >
            {{ i18n(translations.gradeWithAITitle) }}
          </h3>
          <p class="body-2 mt-16 text-center">
            {{ i18n(translations.gradeWithAIModalDescription) }}
          </p>

          <div class="bg-surface-default-low rounded-lg m-28 p-10">
            <RevIllustration
              v-if="qrCodeUrl"
              alt="app-funnel-qr-code"
              class="size-[140px]"
              :height="140"
              :src="qrCodeUrl"
              :width="140"
            />
            <RevSkeleton v-else height="140" shape="rectangle" width="140" />
          </div>
          <div class="flex justify-center">
            <AppStoreButtons
              campaign="grading-with-ai"
              tracking-zone="grading-with-ai"
            />
          </div>
        </RevCard>
      </template>
    </RevDrawer>

    <div class="mt-12" data-test="grade-with-ai">
      <div class="w-full" :class="$style.buttonWrapper">
        <RevButtonBase
          class="bg-surface-default-low rounded-lg relative z-10 m-1 w-full items-start border-transparent p-20 hover:!bg-static-default-low-hover"
          :to="
            breakpoint < Breakpoint.LG ? appsflyerLinkWithPayload : undefined
          "
          @click="handleGradeWithAI"
        >
          <span class="flex w-full items-start">
            <IconSparkles class="size-24" :height="24" :width="24" />

            <span class="flex flex-col pl-8 text-left">
              <span class="text-static-default-hi body-1-bold">
                {{ i18n(translations.gradeWithAIOpenApp) }}
              </span>

              <span class="text-static-default-mid body-2">
                {{ i18n(translations.gradeWithAIGetStarted) }}
              </span>
            </span>

            <IconChevronRight
              class="my-auto ml-auto hidden size-24 md:block"
              :height="24"
              :width="24"
            />
          </span>
        </RevButtonBase>
      </div>

      <div class="hidden items-center py-24 md:flex">
        <RevDivider class="h-1 w-full" />

        <p class="px-12">{{ i18n(translations.gradeWithAIOr) }}</p>

        <RevDivider class="h-1 w-full" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useRouteParams, useRouteQuery } from '#imports'
import { computed, onMounted, ref } from 'vue'

import AppStoreButtons from '@backmarket/nuxt-layer-base/components/AppStoreButtons/AppStoreButtons.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevCard } from '@ds/components/Card'
import { RevDivider } from '@ds/components/Divider'
import { RevDrawer } from '@ds/components/Drawer'
import { RevIllustration } from '@ds/components/Illustration'
import { openModal } from '@ds/components/ModalBase'
import { RevSkeleton } from '@ds/components/Skeleton'
import { IconChevronRight } from '@ds/icons/IconChevronRight'
import { IconSparkles } from '@ds/icons/IconSparkles'
import QRCode from 'qrcode'

import { MODAL_NAMES } from '../../../../constants'

import translations from './GradingwithAI.translations'

defineProps<{
  isStepComplete: boolean
}>()

const routeParams = useRouteParams()
const routeQuery = useRouteQuery()

const appsflyerLinkWithPayload = computed(() => {
  // eslint-disable-next-line camelcase
  const { nextStep, state_screen, ...cleaned } = routeQuery
  const stringifiedParams = { category: routeParams.id, ...cleaned }
  const appsflyerLink = 'https://app.backmarket.com/j4lA/kbn7ig6s?af_qr=true'

  return `${appsflyerLink}&${new URLSearchParams(stringifiedParams).toString()}`
})

const i18n = useI18n()
const breakpoint = useClientOnlyComputedBreakpoint()
const qrCodeUrl = ref('')

function handleGradeWithAI() {
  if (breakpoint.value >= Breakpoint.LG) {
    openModal(MODAL_NAMES.GRADING_WITH_IA)
  }
}

onMounted(async () => {
  qrCodeUrl.value = await QRCode.toDataURL(appsflyerLinkWithPayload.value)
})
</script>

<style lang="css" module>
.buttonWrapper {
  @apply w-full rounded-[14px] relative overflow-hidden flex items-center justify-center;

  @media (max-width: theme('screens.lg')) {
    box-shadow:
      0 0 20px 1px rgba(238, 230, 237, 0.8),
      0 0 20px 0 rgba(255, 72, 140, 0.5),
      0 0 20px 3px rgba(226, 247, 126, 0.5),
      0 0 20px 5px rgba(163, 102, 232, 0.3);
  }
}

.buttonWrapper::before {
  content: '';
  @apply absolute block h-[900px] w-[900px] z-0;
  background: linear-gradient(
    180deg,
    #e2f77e 10%,
    #e7729e 33%,
    #fbf8ff 66%,
    #a366e8 100%
  );
  animation: rotate 5s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
